import React, { useEffect, useMemo, useRef, useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Trophy, RotateCcw, Check, X, AlertTriangle, FileDown, Pencil, SlidersHorizontal, Ruler, Calculator, ClipboardList, Truck, Warehouse, FlaskConical, Package, Coins, Lock, PieChart, Grip, BarChart3, Sparkles, Tag, ArrowRight } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { computeProduct, isComputable, chf, fmt, num, DEFAULT_PARAMS, effectiveConsumption, modeFor, modeForProduct, computePack, isPackComputable } from "@/lib/calc";
import { SmartAreaButton } from "@/components/AreaCalculator";
import { getSimilar, translateText } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

// Backend equivalence criteria keys -> i18n reason labels (shared by banner + PDF)
const EQUIV_REASON_KEYS = { subcategory: "reasonType", resistenza: "reasonRes", rapida: "reasonRapid", componenti: "reasonComp" };
const equivReasonText = (keys, t) => (keys || []).map((k) => t(EQUIV_REASON_KEYS[k] || k));

// Mechanical resistance in MPa derived from normative class (screed C25/C30,
// repair R1-R4 per EN 1504-3). Returns 0 when no class is declared.
function resMPa(spec) {
  const r = (spec.resistenza || "").toString().toUpperCase();
  const c = r.match(/C\s?(\d{2})/);
  if (c) return parseInt(c[1], 10);
  const rip = (spec.classe_ripristino || "").toString().toUpperCase().match(/R\s?([1-4])/);
  if (rip) return ({ "1": 10, "2": 15, "3": 25, "4": 45 })[rip[1]] || 0;
  return 0;
}

// Deformability class (colle) mapped to a 0-100 scale so it can drive a bar:
// No S = 0, S1 = 50, S2 = 100. `decl` = the class was actually declared.
function elasticClass(spec) {
  const raw = (spec.resistenza || "").toString().trim();
  const r = raw.toUpperCase().replace(/NO?N?\s*S[12]/g, "");
  if (/S2/.test(r)) return { v: 100, txt: "S2", decl: true };
  if (/S1/.test(r)) return { v: 50, txt: "S1", decl: true };
  const decl = raw !== "" && raw !== "-";
  return { v: 0, txt: "No S", decl };
}

const _REPAIR_SUBS = ["ripristino_edilizia", "ripristino_stradale", "massetti", "autolivellanti"];
const _GLUE_SUBS = ["adesivi_standard", "adesivi_reattivi", "colle_parquet", "colle_pvc_lvt"];

// Category-aware 8th chart so the grid never shows an empty slot.
function buildDynamicMetric(specA, specB, productA, productB, t) {
  const subs = [productA.subcategory, productB.subcategory];
  const group = subs.some((s) => _REPAIR_SUBS.includes(s)) ? "repair"
    : subs.some((s) => _GLUE_SUBS.includes(s)) ? "glue" : "other";

  if (group === "repair") {
    const a = resMPa(specA), b = resMPa(specB);
    if (a <= 0 && b <= 0) return null;
    return { key: "res", label: t("axResMpa"), abbr: t("rAxRes"), a, b, better: "high",
      fmt: (v) => fmt(v, 0) + " MPa", declA: a > 0, declB: b > 0 };
  }
  if (group === "glue") {
    const ea = elasticClass(specA), eb = elasticClass(specB);
    return { key: "elast", label: t("axElasticita"), abbr: t("rAxElast"), a: ea.v, b: eb.v, better: "high",
      fmt: (v) => (v >= 100 ? "S2" : v >= 50 ? "S1" : "No S"), declA: ea.decl, declB: eb.decl };
  }
  // other categories: density if present, otherwise hide the whole final block
  const da = num(specA.densita_kg_dm3), db = num(specB.densita_kg_dm3);
  if (da <= 0 && db <= 0) return null;
  return { key: "dens", label: t("axDensita"), abbr: t("rAxDens"), a: da, b: db, better: "high", neutral: true,
    fmt: (v) => fmt(v, 2) + " kg/dm³", declA: da > 0, declB: db > 0 };
}

// technical-data row registry: how to read each field from a spec
const ROW_DEFS = {
  resistenza: { val: (s) => s.resistenza },
  certificazioni: { val: (s) => s.certificazioni },
  colore: { val: (s) => s.colore },
  acqua: { val: (s) => rangeVal(s.acqua_min_l, s.acqua_max_l) },
  spessore: { val: (s) => rangeVal(s.spessore_min_mm, s.spessore_max_mm) },
  lavorabilita: { val: (s) => s.lavorabilita_min },
  pedonabile: { val: (s) => s.pedonabile_ore },
  attesaPiastrelle: { val: (s) => s.attesa_piastrelle_giorni },
  attesaParquet: { val: (s) => s.attesa_parquet_giorni },
  maniProdotto: { val: (s) => s.mani_prodotto },
  densita: { val: (s) => s.densita_kg_dm3 },
  allungamento: { val: (s) => s.allungamento_pct },
  classeRipristino: { val: (s) => s.classe_ripristino },
  base_legante: { val: (s) => s.base_legante },
  note_consumo: { val: (s) => s.note_consumo },
  base_chimica: { val: (s) => s.base_chimica },
  tipologia: { val: (s) => s.tipologia },
  diluizione: { val: (s) => s.diluizione },
  consistenza: { val: (s) => s.consistenza },
  comportamento_elastico: { val: (s) => s.comportamento_elastico },
  funzione: { val: (s) => s.funzione },
  modulo_elastico: { val: (s) => (s.modulo_elastico != null ? `${s.modulo_elastico} GPa` : "") },
  shore_a: { val: (s) => s.shore_a },
  capacita_movimento: { val: (s) => (s.capacita_movimento != null ? `±${s.capacita_movimento}%` : "") },
  gel_time_min: { val: (s) => (s.gel_time_min != null ? `${s.gel_time_min} min` : "") },
  indurimento: { val: (s) => s.indurimento },
  larghezza_rotolo: { val: (s) => (s.larghezza_rotolo != null ? `${s.larghezza_rotolo} m` : "") },
  spessore_telo: { val: (s) => (s.spessore_telo != null ? `${s.spessore_telo} mm` : "") },
  pompabile: { bool: true, val: (s) => s.pompabile },
  pendenze: { bool: true, val: (s) => s.pendenze },
  idrorepellente: { bool: true, val: (s) => s.idrorepellente },
  piscine: { bool: true, val: (s) => s.piscine },
  fondi_assorbenti: { bool: true, val: (s) => s.fondi_assorbenti },
  fondi_non_assorbenti: { bool: true, val: (s) => s.fondi_non_assorbenti },
  barriera_vapore: { bool: true, val: (s) => s.barriera_vapore },
  fibrorinforzato: { bool: true, val: (s) => s.fibrorinforzato },
  interno: { bool: true, val: (s) => s.interno },
  esterno: { bool: true, val: (s) => s.esterno },
  serpentine: { bool: true, val: (s) => s.serpentine },
  rapida: { bool: true, val: (s) => s.rapida },
  ceramica: { bool: true, val: (s) => s.ceramica },
  mosaico: { bool: true, val: (s) => s.mosaico },
  pietra_naturale: { bool: true, val: (s) => s.pietra_naturale },
  parquet: { bool: true, val: (s) => s.parquet },
  pareti: { bool: true, val: (s) => s.pareti },
  pavimenti: { bool: true, val: (s) => s.pavimenti },
  soffitto: { bool: true, val: (s) => s.soffitto },
  // computed clarity rows (need t): where a product can be used
  ambito: { val: (s, t) => ambitoText(s, t) },
  applicazione: { val: (s, t) => applicazioneText(s, t) },
};

// relevant technical fields per subcategory (keeps the table contextual)
const TECH_BY_SUB = {
  autolivellanti: ["certificazioni", "base_legante", "spessore", "acqua", "lavorabilita", "pedonabile", "attesaPiastrelle", "attesaParquet", "pompabile", "serpentine", "ambito"],
  massetti: ["certificazioni", "tipologia", "spessore", "acqua", "lavorabilita", "pedonabile", "attesaPiastrelle", "attesaParquet", "serpentine", "ambito"],
  rasanti: ["certificazioni", "spessore", "acqua", "lavorabilita", "pedonabile", "attesaPiastrelle", "pendenze", "applicazione", "ambito"],
  primer: ["base_chimica", "diluizione", "fondi_assorbenti", "fondi_non_assorbenti", "barriera_vapore", "certificazioni", "interno", "esterno", "serpentine"],
  stucchi_fughe: ["certificazioni", "base_chimica", "colore", "densita", "lavorabilita", "pedonabile", "idrorepellente", "piscine", "interno", "esterno"],
  imperm_liquidi: ["certificazioni", "tipologia", "spessore", "maniProdotto", "attesaPiastrelle", "interno", "esterno", "note_consumo"],
  malte_elastiche: ["certificazioni", "tipologia", "spessore", "acqua", "maniProdotto", "attesaPiastrelle", "interno", "esterno", "note_consumo"],
  teli_accessori: ["funzione", "spessore_telo", "larghezza_rotolo", "allungamento", "interno", "esterno"],
  colle_parquet: ["certificazioni", "base_chimica", "comportamento_elastico", "lavorabilita", "pedonabile", "attesaParquet", "serpentine", "interno", "parquet"],
  colle_pvc_lvt: ["base_chimica", "lavorabilita", "pedonabile", "fibrorinforzato", "serpentine", "interno"],
  ripristino_edilizia: ["certificazioni", "classeRipristino", "consistenza", "spessore", "acqua", "lavorabilita", "modulo_elastico", "applicazione", "ambito"],
  ripristino_stradale: ["certificazioni", "classeRipristino", "consistenza", "spessore", "acqua", "lavorabilita", "modulo_elastico", "applicazione", "ambito"],
  protezione_ferri: ["certificazioni", "tipologia", "maniProdotto", "interno", "esterno"],
  ancoraggi_sigillanti: ["certificazioni", "base_chimica", "gel_time_min", "indurimento", "shore_a", "capacita_movimento", "allungamento", "interno", "esterno"],
};

// tile adhesives (colle piastrelle) keep the full legacy set, as requested
const TECH_LEGACY = [
  "resistenza", "certificazioni", "colore", "acqua", "spessore", "lavorabilita", "pedonabile",
  "interno", "esterno", "serpentine", "rapida", "ceramica", "mosaico", "pietra_naturale",
  "parquet", "pareti", "pavimenti", "soffitto", "note_consumo",
];

const COST_COLORS = {
  product: "hsl(var(--primary))",
  transport: "hsl(var(--info))",
  mixing: "#22c55e",
  pallet: "#a855f7",
};

function useFlash(value) {
  const ref = useRef(value);
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    if (ref.current !== value) {
      ref.current = value;
      setFlash(true);
      const id = setTimeout(() => setFlash(false), 600);
      return () => clearTimeout(id);
    }
  }, [value]);
  return flash;
}

export default function ComparisonView({ productA, productB, onPickB, matchedB }) {
  const { t, setView, setQuoteDraft } = useApp();
  const [params, setParams] = useState(DEFAULT_PARAMS);
  const [ovA, setOvA] = useState({});
  const [ovB, setOvB] = useState({});

  useEffect(() => { setOvA({}); }, [productA?.id]);
  useEffect(() => { setOvB({}); }, [productB?.id]);

  const specA = useMemo(() => ({ ...(productA?.specs || {}), ...ovA }), [productA, ovA]);
  const specB = useMemo(() => ({ ...(productB?.specs || {}), ...ovB }), [productB, ovB]);

  const modeA = modeForProduct(specA, productA?.subcategory);
  const modeB = modeForProduct(specB, productB?.subcategory);
  const mode = modeA; // reference for shared labels/export
  const packMode = modeA === "pack" && modeB === "pack";
  const pA = useMemo(() => ({ ...params, mode: modeA }), [params, modeA]);
  const pB = useMemo(() => ({ ...params, mode: modeB }), [params, modeB]);
  const okA = isComputable(specA, modeA);
  const okB = isComputable(specB, modeB);

  const resA = useMemo(() => computeProduct(specA, pA), [specA, pA]);
  const resB = useMemo(() => computeProduct(specB, pB), [specB, pB]);

  // Equivalence verdict + suggested match come from the backend (single source
  // of truth) so the live banner, the dropdown badge and the PDF export agree.
  const [equivInfo, setEquivInfo] = useState({ equivalent: true, reasons: [], match: null });
  useEffect(() => {
    if (!(productA && productB)) { setEquivInfo({ equivalent: true, reasons: [], match: null }); return; }
    let alive = true;
    getSimilar(productA.id, productB.brand, productB.id)
      .then((r) => {
        if (!alive) return;
        const reasons = r.reasons || [];
        setEquivInfo({
          equivalent: r.equivalent !== false && reasons.length === 0,
          reasons,
          match: r.match && r.match.id !== productB.id ? r.match : null,
        });
      })
      .catch(() => { if (alive) setEquivInfo({ equivalent: true, reasons: [], match: null }); });
    return () => { alive = false; };
  }, [productA?.id, productB?.id, productB?.brand]);

  const bothOk = okA && okB && num(params.area_m2) > 0;
  const winner = bothOk ? (resA.total <= resB.total ? "a" : "b") : null;
  const diff = Math.abs(resA.total - resB.total);
  const cheaper = winner === "a" ? productA : productB;
  const savingsPct = bothOk && Math.max(resA.total, resB.total) > 0
    ? (diff / Math.max(resA.total, resB.total)) * 100 : 0;

  const setParam = (k, v) => setParams((p) => ({ ...p, [k]: v }));

  // Carry a product (with its market-edited price + current params) into the quote builder
  const goToQuote = (product, spec, opts = {}) => {
    if (!product) return;
    const m = modeForProduct(spec, product.subcategory);
    const isPack = m === "pack";
    const price = isPack ? num(spec.pack_price) : num(spec.price_per_kg);
    setQuoteDraft({
      product,
      spec: { ...spec },
      price,
      area: opts.area != null ? num(opts.area) : num(params.area_m2),
      thickness: num(params.thickness),
      params: { ...params },
    });
    setView("quote");
  };

  // Single-product mode: full config + tech data for one product, ready to quote
  const single = !(productA && productB);
  const solo = productA || productB;
  const soloIsA = !!productA;
  const soloSpec = soloIsA ? specA : specB;
  const soloOv = soloIsA ? ovA : ovB;
  const setSoloOv = soloIsA ? setOvA : setOvB;

  if (single && solo) {
    return (
      <SingleView
        product={solo}
        spec={soloSpec}
        ov={soloOv}
        setOv={setSoloOv}
        params={params}
        setParam={setParam}
        t={t}
        onGenerateQuote={goToQuote}
      />
    );
  }

  const exportPDF = () => {
    const doc = new jsPDF();
    const W = doc.internal.pageSize.getWidth();
    doc.setFillColor(255, 192, 0);
    doc.rect(0, 0, W, 22, "F");
    doc.setTextColor(17, 17, 17);
    doc.setFontSize(18);
    doc.setFont(undefined, "bold");
    doc.text("METEROX", 14, 14);
    doc.setFontSize(10);
    doc.setFont(undefined, "normal");
    doc.text(t("comparison"), W - 14, 14, { align: "right" });
    doc.setTextColor(60, 60, 60);
    doc.setFontSize(9);
    doc.text(new Date().toLocaleString(), 14, 30);

    const A = `${productA.brand} ${productA.name}`;
    const B = `${productB.brand} ${productB.name}`;

    // ---- feature metrics, wins, equivalence, radar (like the web page) ----
    const anyPack = resA.isPack || resB.isPack;
    const M = [
      { label: t("pricePerKg"), abbr: "CHF/kg", key: "priceKg", a: num(specA.price_per_kg), b: num(specB.price_per_kg), better: "low", f: (v) => chf(v) },
      { label: t("costPerM2"), abbr: "CHF/m2", key: "costM2", a: resA.costPerM2, b: resB.costPerM2, better: "low", f: (v) => chf(v) },
      { label: t("resa"), abbr: t("rAxYield"), key: "resa", a: resA.resa, b: resB.resa, better: "high", f: (v) => fmt(v, 2) },
      { label: t("consumption"), abbr: t("rAxConsumo"), key: "consumo", a: resA.consumption, b: resB.consumption, better: "low", f: (v) => fmt(v, 2) },
      { label: t("lavorabilita"), abbr: t("rAxWork"), key: "lav", a: num(specA.lavorabilita_min), b: num(specB.lavorabilita_min), better: "high", f: (v) => fmt(v, 0) },
      { label: t("pedonabile"), abbr: t("rAxWalk"), key: "ped", a: num(specA.pedonabile_ore), b: num(specB.pedonabile_ore), better: "low", f: (v) => fmt(v, 0) },
      { label: t("axResMpa"), abbr: t("rAxRes"), key: "res", a: resMPa(specA), b: resMPa(specB), better: "high", f: (v) => fmt(v, 0) },
      { label: t("axThickMm"), abbr: t("rAxThick"), key: "spess", a: num(specA.spessore_max_mm), b: num(specB.spessore_max_mm), better: "high", f: (v) => fmt(v, 0) },
    ]
      .filter((m) => !(anyPack && (m.key === "priceKg" || m.key === "resa" || m.key === "consumo")))
      .filter((m) => m.a > 0 || m.b > 0);
    let wA = 0, wB = 0;
    M.forEach((m) => {
      if (!(m.a > 0 && m.b > 0)) return;
      const aw = m.better === "low" ? m.a < m.b : m.a > m.b;
      const bw = m.better === "low" ? m.b < m.a : m.b > m.a;
      if (aw) wA += 1; else if (bw) wB += 1;
    });
    const reasons = equivReasonText(equivInfo.reasons, t);
    const equiv = equivInfo.equivalent;

    if (bothOk) {
      doc.setTextColor(0, 150, 60);
      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      doc.text(`${t("winner")}: ${winner === "a" ? A : B}  ·  ${t("finalCost")} ${chf(winner === "a" ? resA.total : resB.total)}  ·  ${t("savings")} ${chf(diff)}`, 14, 39);
      doc.setTextColor(60, 60, 60);
      doc.setFontSize(9);
      doc.setFont(undefined, "normal");
      doc.text(`VS  ${A}: ${wA}   —   ${B}: ${wB}   (${t("versusTitle")})`, 14, 45);
      const eqText = equiv ? t("equivSame") : `${t("equivDiff")} (${reasons.join(", ")}).`;
      doc.text(doc.splitTextToSize(eqText, W - 28), 14, 50);
    }

    autoTable(doc, {
      startY: bothOk ? 56 : 46,
      head: [[`${t("metratura")}: ${fmt(params.area_m2, 0)} m2${mode === "notch" ? ` · ${t("trowel")} ${num(params.notch) || 6}mm` : mode === "thickness" ? ` · ${t("spessore")} ${num(params.thickness) || 1}mm` : mode === "grout" ? ` · ${fmt(params.tileA, 0)}x${fmt(params.tileB, 0)}cm · ${t("jointWidth")} ${num(params.jointWidth) || 3}mm` : ""}`, A, B]],
      body: [
        [t("totalKg"), okA ? fmt(resA.totalKg, 0) + " kg" : "-", okB ? fmt(resB.totalKg, 0) + " kg" : "-"],
        [t("bagsNeeded"), okA ? fmt(resA.bags, 0) : "-", okB ? fmt(resB.bags, 0) : "-"],
        [t("resa"), okA ? fmt(resA.resa, 2) : "-", okB ? fmt(resB.resa, 2) : "-"],
        [t("productCost"), okA ? chf(resA.productCost) : "-", okB ? chf(resB.productCost) : "-"],
        [t("transportCost"), okA ? chf(resA.transportCost) : "-", okB ? chf(resB.transportCost) : "-"],
        [t("mixingCost"), okA ? chf(resA.mixingCost) : "-", okB ? chf(resB.mixingCost) : "-"],
        [t("palletCost"), okA ? chf(resA.palletCost) : "-", okB ? chf(resB.palletCost) : "-"],
        [t("totalCost"), okA ? chf(resA.total) : "-", okB ? chf(resB.total) : "-"],
        [t("costPerM2"), okA ? chf(resA.costPerM2) : "-", okB ? chf(resB.costPerM2) : "-"],
      ],
      theme: "grid",
      headStyles: { fillColor: [17, 17, 17], textColor: 255, fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      columnStyles: { 1: { halign: "right" }, 2: { halign: "right" } },
    });

    const yn = (v) => (v === true || v === "true" ? t("yes") : t("no"));
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 6,
      head: [[t("techData"), A, B]],
      body: [
        [t("resistenza"), specA.resistenza || "-", specB.resistenza || "-"],
        [t("certificazioni"), specA.certificazioni || "-", specB.certificazioni || "-"],
        [t("colore"), specA.colore || "-", specB.colore || "-"],
        [t("acqua"), rangeVal(specA.acqua_min_l, specA.acqua_max_l) || "-", rangeVal(specB.acqua_min_l, specB.acqua_max_l) || "-"],
        [t("spessore"), rangeVal(specA.spessore_min_mm, specA.spessore_max_mm) || "-", rangeVal(specB.spessore_min_mm, specB.spessore_max_mm) || "-"],
        [t("lavorabilita"), specA.lavorabilita_min ?? "-", specB.lavorabilita_min ?? "-"],
        [t("pedonabile"), specA.pedonabile_ore ?? "-", specB.pedonabile_ore ?? "-"],
        [t("interno"), yn(specA.interno), yn(specB.interno)],
        [t("esterno"), yn(specA.esterno), yn(specB.esterno)],
        [t("serpentine"), yn(specA.serpentine), yn(specB.serpentine)],
        [t("rapida"), yn(specA.rapida), yn(specB.rapida)],
        [t("pietra_naturale"), yn(specA.pietra_naturale), yn(specB.pietra_naturale)],
      ],
      theme: "grid",
      headStyles: { fillColor: [17, 17, 17], textColor: 255, fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      columnStyles: { 1: { halign: "right" }, 2: { halign: "right" } },
    });

    // feature comparison table (VS)
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 6,
      head: [[t("versusTitle"), A, B, t("winner")]],
      body: M.map((m) => {
        const aw = m.a > 0 && m.b > 0 && (m.better === "low" ? m.a < m.b : m.a > m.b);
        const bw = m.a > 0 && m.b > 0 && (m.better === "low" ? m.b < m.a : m.b > m.a);
        return [m.label, m.a > 0 ? m.f(m.a) : "-", m.b > 0 ? m.f(m.b) : "-", aw ? A : (bw ? B : "=")];
      }),
      theme: "grid",
      headStyles: { fillColor: [17, 17, 17], textColor: 255, fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      columnStyles: { 1: { halign: "right" }, 2: { halign: "right" } },
    });

    // radar chart (canvas -> image)
    function radarDataUrl(metrics) {
      if (metrics.length < 3) return null;
      const S = 300, c = S / 2, R = 110, n = metrics.length;
      const cv = document.createElement("canvas"); cv.width = S; cv.height = S;
      const x = cv.getContext("2d"); x.fillStyle = "#fff"; x.fillRect(0, 0, S, S);
      const norm = (m, side) => { const mx = Math.max(m.a, m.b) || 1, mn = Math.min(m.a, m.b); const v = side === "a" ? m.a : m.b; if (!v) return 0.08; const r = m.better === "high" ? v / mx : (mn || v) / v; return Math.max(0.08, Math.min(1, r)); };
      const pt = (i, r) => { const a = -Math.PI / 2 + i * 2 * Math.PI / n; return [c + R * r * Math.cos(a), c + R * r * Math.sin(a)]; };
      x.strokeStyle = "#dddddd"; x.lineWidth = 1;
      [0.33, 0.66, 1].forEach((g) => { x.beginPath(); metrics.forEach((_, i) => { const [px, py] = pt(i, g); i ? x.lineTo(px, py) : x.moveTo(px, py); }); x.closePath(); x.stroke(); });
      metrics.forEach((_, i) => { const [px, py] = pt(i, 1); x.beginPath(); x.moveTo(c, c); x.lineTo(px, py); x.stroke(); });
      const draw = (side, stroke, fill) => { x.beginPath(); metrics.forEach((m, i) => { const [px, py] = pt(i, norm(m, side)); i ? x.lineTo(px, py) : x.moveTo(px, py); }); x.closePath(); x.fillStyle = fill; x.fill(); x.strokeStyle = stroke; x.lineWidth = 2; x.stroke(); };
      draw("a", "#1F8FFF", "rgba(31,143,255,0.25)");
      draw("b", "#FFC000", "rgba(255,192,0,0.25)");
      x.fillStyle = "#666666"; x.font = "10px sans-serif"; x.textAlign = "center";
      metrics.forEach((m, i) => { const [px, py] = pt(i, 1.14); x.fillText((m.abbr || "").slice(0, 16), px, py); });
      return cv.toDataURL("image/png");
    }
    try {
      const url = radarDataUrl(M);
      if (url) {
        let y = doc.lastAutoTable.finalY + 8;
        if (y > 195) { doc.addPage(); y = 20; }
        doc.setFontSize(10); doc.setFont(undefined, "bold"); doc.setTextColor(17, 17, 17);
        doc.text(t("radarTitle"), 14, y);
        doc.addImage(url, "PNG", (W - 90) / 2, y + 4, 90, 90);
        doc.setFontSize(8); doc.setFont(undefined, "normal"); doc.setTextColor(60, 60, 60);
        doc.text(`[A] ${A}`, (W - 90) / 2, y + 99);
        doc.text(`[B] ${B}`, (W - 90) / 2 + 46, y + 99);
      }
    } catch (e) { /* radar optional */ }

    doc.save(`METEROX_${productA.name}_vs_${productB.name}.pdf`.replace(/\s+/g, "_"));
  };

  return (
    <div data-testid="comparison-view" className="space-y-4">
      {!packMode && (
      <div className="flex justify-end">
        <Button
          data-testid="export-pdf-btn"
          onClick={exportPDF}
          variant="outline"
          className="rounded-none border-border font-semibold"
        >
          <FileDown className="mr-2 h-4 w-4" /> {t("exportPdf")}
        </Button>
      </div>
      )}

      {/* Equivalence banner — right below product selection */}
      <EquivalenceBanner info={equivInfo} t={t} onPickB={onPickB} />

      {matchedB && (
        <div className="flex items-start gap-2 border border-info/40 bg-info/10 px-3 py-2.5 text-sm text-foreground" data-testid="match-info-box">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-info" />
          <span>{t("matchInfo")}</span>
        </div>
      )}

      {/* Pack-based comparison (cartridges / sausages / rolls) */}
      {packMode && (
        <PackComparison specA={specA} specB={specB} productA={productA} productB={productB} t={t} onGenerateQuote={goToQuote} />
      )}

      {/* Parameters — always visible, with explanations */}
      {!packMode && (
      <div className="border border-border bg-card" data-testid="site-params">
        <div className="flex flex-wrap items-center gap-2 border-b border-border bg-surface px-4 py-2.5">
          <SlidersHorizontal className="h-4 w-4 text-primary" />
          <span className="label-caps text-foreground">{t("advanced")}</span>
          <span className="ml-auto flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-1 text-[11px] font-semibold text-primary">
            <Pencil className="h-3 w-3" /> {t("editHint")}
          </span>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {(modeA === "notch" || modeB === "notch") && (
              <TrowelCard label={t("trowel")} desc={t("trowelDesc")} unit={t("unitMm")} value={params.notch} onChange={(v) => setParam("notch", v)} />
            )}
            {(modeA === "thickness" || modeB === "thickness") && (
              <ParamCard testid="input-thickness" icon={Ruler} label={t("spessore")} unit={t("unitMmFull")} value={params.thickness} onChange={(v) => setParam("thickness", v)} />
            )}
            {(modeA === "grout" || modeB === "grout") && (
              <>
                <ParamCard testid="input-tilea" icon={Grip} label={t("tileA")} desc={t("groutDesc")} unit={t("unitCm")} value={params.tileA} onChange={(v) => setParam("tileA", v)} />
                <ParamCard testid="input-tileb" icon={Grip} label={t("tileB")} unit={t("unitCm")} value={params.tileB} onChange={(v) => setParam("tileB", v)} />
                <ParamCard testid="input-tilethickness" icon={Ruler} label={t("tileThickness")} unit={t("unitMmFull")} value={params.tileThickness} onChange={(v) => setParam("tileThickness", v)} />
                <ParamCard testid="input-jointwidth" icon={Ruler} label={t("jointWidth")} unit={t("unitMmFull")} value={params.jointWidth} onChange={(v) => setParam("jointWidth", v)} />
              </>
            )}
            <ParamCard testid="input-metratura" icon={Ruler} label={t("metratura")} unit={t("unitM2")} value={params.area_m2} onChange={(v) => setParam("area_m2", v)} action={<SmartAreaButton onResult={(v) => setParam("area_m2", v)} />} highlight />
          </div>
          <div className="border-t border-dashed border-border pt-3">
            <span className="label-caps text-muted-foreground">{t("laborSection")}</span>
            <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <ParamCard testid="input-salary" icon={Coins} label={t("salary")} desc={t("descSalary")} unit={t("unitChfHour")} value={params.salary} onChange={(v) => setParam("salary", v)} />
              <ParamCard testid="input-transport" icon={Truck} label={t("transportMin")} desc={t("descTransport")} unit={t("unitMinBag")} value={params.transportMin} onChange={(v) => setParam("transportMin", v)} />
              <ParamCard testid="input-mixing" icon={FlaskConical} label={t("mixingMin")} desc={t("descMixing")} unit={t("unitMinBag")} value={params.mixingMin} onChange={(v) => setParam("mixingMin", v)} />
              <ParamCard testid="input-pallet" icon={Warehouse} label={t("palletMin")} desc={t("descPallet")} unit={t("unitMinPallet")} value={params.palletMin} onChange={(v) => setParam("palletMin", v)} />
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Per-product price (editable) + fixed product data */}
      {!packMode && (
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <EditPanel side="a" product={productA} spec={specA} ov={ovA} setOv={setOvA} ok={okA} t={t} cparams={pA} onGenerateQuote={() => goToQuote(productA, specA)} />
        <EditPanel side="b" product={productB} spec={specB} ov={ovB} setOv={setOvB} ok={okB} t={t} cparams={pB} onGenerateQuote={() => goToQuote(productB, specB)} />
      </div>
      )}

      {/* Cost composition — the 2 bars, placed right above the comparison */}
      {!packMode && bothOk && (
        <CostComposition resA={resA} resB={resB} productA={productA} productB={productB} t={t} />
      )}

      {/* Cost breakdown */}
      {!packMode && (
      <div className="border border-border bg-card overflow-x-auto">
        <div className="min-w-[480px]">
        <div className="grid grid-cols-[1.4fr,1fr,1fr] border-b border-border bg-surface">
          <div className="flex items-center gap-2 px-4 py-2.5 label-caps text-muted-foreground"><Calculator className="h-4 w-4 text-primary" />{t("comparison")}</div>
          <HeaderCol name={productA?.name} accent="text-info" isWinner={winner === "a"} />
          <HeaderCol name={productB?.name} accent="text-primary" isWinner={winner === "b"} />
        </div>

        <CostRow testid="row-totalkg" label={t("totalKg")} a={resA.isPack ? "—" : fmt(resA.totalKg, 0) + " kg"} b={resB.isPack ? "—" : fmt(resB.totalKg, 0) + " kg"} okA={okA} okB={okB} />
        <CostRow testid="row-bags" label={t("bagsNeeded")} a={fmt(resA.bags, 0)} b={fmt(resB.bags, 0)} okA={okA} okB={okB} />
        <CostRow testid="row-resa" label={t("resa")} a={resA.isPack ? "—" : fmt(resA.resa, 2)} b={resB.isPack ? "—" : fmt(resB.resa, 2)} okA={okA} okB={okB} />
        <Divider />
        <CostRow testid="row-productcost" icon={Package} color={COST_COLORS.product} label={t("productCost")} a={chf(resA.productCost)} b={chf(resB.productCost)} okA={okA} okB={okB} lowA={resA.productCost} lowB={resB.productCost} compare />
        <CostRow icon={Truck} color={COST_COLORS.transport} label={t("transportCost")} a={chf(resA.transportCost)} b={chf(resB.transportCost)} okA={okA} okB={okB} lowA={resA.transportCost} lowB={resB.transportCost} compare />
        <CostRow icon={FlaskConical} color={COST_COLORS.mixing} label={t("mixingCost")} a={chf(resA.mixingCost)} b={chf(resB.mixingCost)} okA={okA} okB={okB} lowA={resA.mixingCost} lowB={resB.mixingCost} compare />
        <CostRow icon={Warehouse} color={COST_COLORS.pallet} label={t("palletCost")} a={chf(resA.palletCost)} b={chf(resB.palletCost)} okA={okA} okB={okB} lowA={resA.palletCost} lowB={resB.palletCost} compare />
        <TotalRow label={t("totalCost")} a={resA.total} b={resB.total} okA={okA} okB={okB} winner={winner} />
        <TotalRow label={t("costPerM2")} a={resA.costPerM2} b={resB.costPerM2} okA={okA} okB={okB} winner={winner} perM2 />
        </div>
      </div>
      )}

      {/* CTA #1 — mid flow, right after the cost summary */}
      {!packMode && bothOk && (
        <InlineQuoteCTA productA={productA} productB={productB} onA={() => goToQuote(productA, specA)} onB={() => goToQuote(productB, specB)} t={t} />
      )}

      {/* Versus feature charts */}
      {!packMode && bothOk && (
        <VersusCharts resA={resA} resB={resB} specA={specA} specB={specB} productA={productA} productB={productB} t={t} onPickB={onPickB} />
      )}

      {/* Verdict banner — placed right after the cost summary, just before the technical table */}
      {!packMode && bothOk && (
        <Verdict cheaper={cheaper} winner={winner} total={winner === "a" ? resA.total : resB.total} diff={diff} pct={savingsPct} t={t} />
      )}

      {/* CTA #2 — after the verdict / charts, before technical data */}
      {productA && productB && (
        <InlineQuoteCTA productA={productA} productB={productB} onA={() => goToQuote(productA, specA)} onB={() => goToQuote(productB, specB)} t={t} />
      )}

      {/* Technical data */}
      <div className="border border-border bg-card overflow-x-auto">
        <div className="min-w-[480px]">
        <div className="grid grid-cols-[1.4fr,1fr,1fr] border-b border-border bg-surface">
          <div className="flex items-center gap-2 px-4 py-2.5 label-caps text-muted-foreground"><ClipboardList className="h-4 w-4 text-primary" />{t("techData")}</div>
          <div className="px-4 py-2.5 text-right font-mono text-xs truncate">{productA?.name}</div>
          <div className="px-4 py-2.5 text-right font-mono text-xs truncate">{productB?.name}</div>
        </div>
        {(TECH_BY_SUB[productA?.subcategory] || TECH_LEGACY).map((id) => {
          const def = ROW_DEFS[id];
          if (!def) return null;
          if (id === "note_consumo") return null; // shown translated in the yield section
          const a = def.val(specA, t), b = def.val(specB, t);
          const empty = (v) => v === undefined || v === null || v === "";
          if (empty(a) && empty(b)) return null;
          return <TechRow key={id} label={t(id)} a={def.bool ? boolVal(a, t) : a} b={def.bool ? boolVal(b, t) : b} bool={def.bool} />;
        })}
        </div>
      </div>

      {/* How the yield (resa) is computed — clear & translated, especially for adhesives */}
      {!packMode && (
        <YieldExplain
          productA={productA} productB={productB} specA={specA} specB={specB}
          resA={resA} resB={resB} params={params} modeA={modeA} modeB={modeB}
          okA={okA} okB={okB} t={t}
        />
      )}

      {/* CTA #3 — near the end, after all the data */}
      {productA && productB && (
        <InlineQuoteCTA productA={productA} productB={productB} onA={() => goToQuote(productA, specA)} onB={() => goToQuote(productB, specB)} t={t} />
      )}
    </div>
  );
}

// Beautiful inline CTA blocks placed at key points of the comparison flow
// to send a product straight to the quote page. Integrated (not fixed).
function InlineQuoteCTA({ productA, productB, onA, onB, t }) {
  if (!productA || !productB) return null;
  const Btn = ({ product, onClick, side, testid }) => {
    const solid = side === "a" ? "bg-info text-white" : "bg-primary text-primary-foreground";
    const dot = side === "a" ? "bg-white/25" : "bg-black/15";
    return (
      <button
        data-testid={testid}
        onClick={onClick}
        className={`group relative flex flex-1 items-center gap-3 overflow-hidden ${solid} px-4 py-3.5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0`}
      >
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center ${dot}`}>
          <Tag className="h-4 w-4" strokeWidth={2.5} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[11px] font-medium uppercase tracking-wide opacity-90">{product.brand} · {product.name}</span>
          <span className="block text-sm font-bold leading-tight">{t("chooseForQuote")}</span>
        </span>
        <ArrowRight className="h-5 w-5 shrink-0 opacity-90 transition-transform group-hover:translate-x-1" />
      </button>
    );
  };
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="inline-quote-cta">
      <Btn product={productA} onClick={onA} side="a" testid="select-quote-a" />
      <Btn product={productB} onClick={onB} side="b" testid="select-quote-b" />
    </div>
  );
}

/* ---------- sub components ---------- */

// Auto-translates datasheet text into the current UI language (cached client + server side).
const _transCache = new Map();
function TranslatedText({ text }) {
  const { lang } = useApp();
  const [out, setOut] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const src = (text || "").trim();
    if (!src) { setOut(""); return; }
    const key = `${lang}::${src}`;
    if (_transCache.has(key)) { setOut(_transCache.get(key)); return; }
    let alive = true;
    setLoading(true);
    setOut("");
    translateText(src, lang)
      .then((tx) => { if (alive) { const v = tx || src; _transCache.set(key, v); setOut(v); } })
      .catch(() => { if (alive) setOut(src); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [text, lang]);
  if (loading && !out) {
    return <span className="inline-block h-3 w-40 max-w-full animate-pulse bg-border align-middle" />;
  }
  return <span>{out || text}</span>;
}

// Build a clear, translated step-by-step explanation of the yield (resa) from datasheet values.
function yieldSteps(spec, res, params, mode, t) {
  const C = num(res?.consumption);
  const W = num(spec.bag_weight_kg);
  if (!C) return null;
  const kg = (v) => `${fmt(v, 2)} kg/m²`;
  const steps = [];
  const resaOv = num(spec.resa_override);
  const spess = num(spec.spessore_2mani_mm);
  const base = num(spec.consumption_kg_per_m2);
  const tbl = spec.consumption_table;
  const hasTable = tbl && typeof tbl === "object"
    && (mode === "notch" || mode === "thickness")
    && Object.values(tbl).some((v) => num(v) > 0);
  const tableStr = (unit) => Object.entries(tbl)
    .filter(([, v]) => num(v) > 0)
    .sort((a, b) => num(a[0]) - num(b[0]))
    .map(([k, v]) => `${k}${unit}=${fmt(num(v), 2)}`)
    .join(" · ");

  if (resaOv > 0 && W > 0) {
    steps.push({ k: t("fromDatasheet"), v: `${fmt(resaOv, 2)} m²/${t("unitPackShort")} · ${t("yieldManual")}` });
  } else if (num(spec.consumo_teorico_mm) > 0 && mode === "notch") {
    const mmBase = num(spec.consumo_teorico_mm);
    const notch = num(params.notch) || 6;
    const cd = num(spec.coef_denti) || 0.5;
    const ci = num(spec.coef_inclinazione) || 0.8;
    steps.push({ k: t("fromDatasheet"), v: `${fmt(mmBase, 2)} kg/m²/mm` });
    steps.push({ k: `${t("consumption")} · ${t("trowel")} ${fmt(notch, 0)} mm`, v: `× ${fmt(notch, 0)} × ${fmt(cd, 2)} (${t("teethShort")}) × ${fmt(ci, 2)} (${t("inclShort")}) = ${kg(C)}` });
  } else if (hasTable) {
    const isThk = mode === "thickness";
    const mm = isThk ? (num(params.thickness) || 1) : (num(params.notch) || 6);
    const exact = Object.keys(tbl).some((k) => num(k) === mm && num(tbl[k]) > 0);
    const dim = isThk ? `${fmt(mm, 1)} mm` : `${t("trowel")} ${fmt(mm, 0)} mm`;
    steps.push({ k: `${t("fromDatasheet")} (kg/m²)`, v: tableStr(isThk ? " mm" : " mm") });
    steps.push({ k: `${t("consumption")} · ${dim}`, v: `${kg(C)}${exact ? "" : " · " + t("interpolated")}` });
  } else if (spess > 0 && base > 0) {
    steps.push({ k: t("fromDatasheet"), v: `${fmt(base, 2)} kg/m² / mm` });
    steps.push({ k: t("consumption"), v: `${fmt(base, 2)} × ${fmt(spess, 1)} mm = ${kg(C)}` });
  } else if (mode === "thickness") {
    steps.push({ k: t("fromDatasheet"), v: `${fmt(base, 2)} kg/m² / mm` });
    steps.push({ k: t("consumption"), v: `${fmt(base, 2)} × ${fmt(num(params.thickness) || 1, 1)} mm = ${kg(C)}` });
  } else if (mode === "flat") {
    steps.push({ k: t("fromDatasheet"), v: kg(C) });
  } else if (mode === "grout") {
    steps.push({ k: t("consumption"), v: `${kg(C)} (${t("groutJoint")})` });
  } else {
    steps.push({ k: `${t("fromDatasheet")} · ${t("trowel")} 6 mm`, v: kg(base) });
    steps.push({ k: t("consumption"), v: `${fmt(base, 2)} × (${fmt(num(params.notch) || 6, 0)} mm ÷ 6 mm) = ${kg(C)}` });
  }

  if (W > 0 && !(resaOv > 0)) {
    steps.push({ k: t("resa"), v: `${fmt(W, 1)} kg ÷ ${kg(C)} = ${fmt(res.resa, 2)} m²/${t("unitPackShort")}` });
  }
  const area = num(params.area_m2);
  if (area > 0) {
    steps.push({
      k: `${t("forArea")} ${fmt(area, 0)} m²`,
      v: `${fmt(area, 0)} × ${kg(C)} = ${fmt(res.totalKg, 1)} kg${res.bags ? ` → ${fmt(res.bags, 1)} ${t("unitPackShort")}` : ""}`,
    });
  }
  return steps;
}

function YieldCol({ product, spec, steps, formula, side, t }) {
  if (!product) return null;
  const note = (spec?.note_consumo || "").trim();
  return (
    <div className={`border border-border border-l-4 ${side === "a" ? "border-l-info" : "border-l-primary"} bg-card`}>
      <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
        <span className={`h-2 w-2 shrink-0 ${side === "a" ? "bg-info" : "bg-primary"}`} />
        <span className="font-mono text-xs truncate">{product.brand} · {product.name}</span>
      </div>
      {steps ? (
        <ol className="divide-y divide-dashed divide-border">
          {steps.map((s, i) => (
            <li key={i} className="flex flex-col gap-0.5 px-4 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
              <span className="label-caps text-muted-foreground">{s.k}</span>
              <span className="font-mono text-sm font-semibold text-foreground">{s.v}</span>
            </li>
          ))}
        </ol>
      ) : (
        <p className="px-4 py-3 text-sm text-muted-foreground">—</p>
      )}
      {formula && (
        <div className="border-t border-border bg-primary/5 px-4 py-3" data-testid={`yield-formula-${side}`}>
          <span className="label-caps text-primary">{t("yieldFormulaLabel")}</span>
          <p className="mt-1 font-mono text-sm font-semibold leading-relaxed text-foreground">{formula}</p>
        </div>
      )}
      {note && (
        <div className="border-t border-dashed border-border bg-surface px-4 py-2.5" data-testid={`note-consumo-${side}`}>
          <span className="label-caps text-muted-foreground">{t("noteConsumoTitle")}</span>
          <p className="mt-1 text-sm leading-relaxed text-foreground"><TranslatedText text={note} /></p>
        </div>
      )}
    </div>
  );
}

// One-line, labelled formula that shows exactly how the effective consumption was obtained.
function yieldFormula(spec, res, params, mode, t) {
  const C = num(res?.consumption);
  if (!C) return null;
  const kg = (v) => `${fmt(v, 2)} kg/m²`;
  const resaOv = num(spec.resa_override);
  const spess = num(spec.spessore_2mani_mm);
  const base = num(spec.consumption_kg_per_m2);
  const tbl = spec.consumption_table;
  const hasTable = tbl && typeof tbl === "object"
    && (mode === "notch" || mode === "thickness")
    && Object.values(tbl).some((v) => num(v) > 0);
  const ds = t("fromDatasheet");
  if (resaOv > 0) return null;
  const mmBase = num(spec.consumo_teorico_mm);
  if (mmBase > 0 && mode === "notch") {
    const notch = num(params.notch) || 6;
    const cd = num(spec.coef_denti) || 0.5;
    const ci = num(spec.coef_inclinazione) || 0.8;
    return `${fmt(mmBase, 2)} kg/m² (${ds}) × ${fmt(notch, 0)} mm (${t("trowel")}) × ${fmt(cd, 2)} (${t("teethShort")}) × ${fmt(ci, 2)} (${t("inclShort")}) = ${kg(C)}`;
  }
  if (hasTable) {
    const isThk = mode === "thickness";
    const mm = isThk ? (num(params.thickness) || 1) : (num(params.notch) || 6);
    const exact = Object.keys(tbl).some((k) => num(k) === mm && num(tbl[k]) > 0);
    const dim = isThk ? `${fmt(mm, 1)} mm` : `${t("trowel")} ${fmt(mm, 0)} mm`;
    return `${kg(C)} (${ds} · ${dim}${exact ? "" : " · " + t("interpolated")})`;
  }
  if (spess > 0 && base > 0) {
    return `${fmt(base, 2)} kg/m²/mm (${ds}) × ${fmt(spess, 1)} mm = ${kg(C)}`;
  }
  if (mode === "thickness") {
    return `${fmt(base, 2)} kg/m²/mm (${ds}) × ${fmt(num(params.thickness) || 1, 1)} mm = ${kg(C)}`;
  }
  if (mode === "flat") return `${kg(C)} (${ds})`;
  if (mode === "grout") return `${kg(C)} (${t("groutJoint")})`;
  // notch linear model (reference at 6 mm)
  const notch = num(params.notch) || 6;
  return `${fmt(base, 2)} kg/m² (${ds} · ${t("trowel")} 6 mm) × ${fmt(notch, 0)}÷6 (${t("trowel")} ${fmt(notch, 0)} mm) = ${kg(C)}`;
}

function YieldExplain({ productA, productB, specA, specB, resA, resB, params, modeA, modeB, okA, okB, t }) {
  const stepsA = okA ? yieldSteps(specA, resA, params, modeA, t) : null;
  const stepsB = okB ? yieldSteps(specB, resB, params, modeB, t) : null;
  const formulaA = okA ? yieldFormula(specA, resA, params, modeA, t) : null;
  const formulaB = okB ? yieldFormula(specB, resB, params, modeB, t) : null;
  const noteA = (specA?.note_consumo || "").trim();
  const noteB = (specB?.note_consumo || "").trim();
  if (!stepsA && !stepsB && !noteA && !noteB) return null;
  return (
    <div className="border border-border bg-card" data-testid="yield-explain">
      <div className="border-b border-border bg-surface px-4 py-2.5">
        <div className="flex items-center gap-2 label-caps text-foreground">
          <Calculator className="h-4 w-4 text-primary" /> {t("yieldTitle")}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{t("yieldSubtitle")}</p>
      </div>
      <div className="grid grid-cols-1 gap-4 p-4 lg:grid-cols-2">
        <YieldCol product={productA} spec={specA} steps={stepsA} formula={formulaA} side="a" t={t} />
        <YieldCol product={productB} spec={specB} steps={stepsB} formula={formulaB} side="b" t={t} />
      </div>
    </div>
  );
}

function Verdict({ cheaper, winner, total, diff, pct, t }) {
  return (
    <div
      data-testid="verdict-banner"
      className="flex flex-wrap items-center gap-x-6 gap-y-3 border border-success bg-success/10 p-4"
    >
      <div className="flex h-12 w-12 shrink-0 items-center justify-center bg-success text-white">
        <Trophy className="h-6 w-6" />
      </div>
      <div className="min-w-0">
        <div className="label-caps text-success">{t("winner")}</div>
        <div className="font-display text-2xl font-black tracking-tight truncate" data-testid="winner-name">
          {cheaper?.brand} {cheaper?.name}
        </div>
      </div>
      <div className="ml-auto flex items-center gap-6">
        <div className="text-right">
          <div className="label-caps text-muted-foreground">{t("finalCost")}</div>
          <div className="font-mono text-2xl font-bold" data-testid="winner-total">{chf(total)}</div>
        </div>
        <div className="h-10 w-px bg-success/40" />
        <div className="text-right">
          <div className="label-caps text-success">{t("savings")}</div>
          <div className="font-mono text-2xl font-bold text-success" data-testid="savings-value">
            −{chf(diff)}
          </div>
          <div className="font-mono text-[11px] text-muted-foreground">−{fmt(pct, 1)}%</div>
        </div>
      </div>
    </div>
  );
}

function HeaderCol({ name, accent, isWinner }) {
  return (
    <div className={`flex items-center justify-end gap-1.5 px-4 py-2.5 text-right font-mono text-xs ${isWinner ? "font-bold" : ""}`}>
      {isWinner && <Trophy className="h-3.5 w-3.5 text-success" />}
      <span className={`truncate ${accent}`}>{name}</span>
    </div>
  );
}

function EditPanel({ side, product, spec, ov, setOv, ok, t, cparams, onGenerateQuote }) {
  if (!product) return null;
  const accent = side === "a" ? "border-l-info" : "border-l-primary";
  const set = (k, v) => setOv((p) => ({ ...p, [k]: v === "" ? "" : num(v) }));

  // Pack products (cartridge / sausage / roll): show pack price + coverage instead of kg fields
  if (cparams.mode === "pack") {
    const cov = num(spec.pack_coverage);
    const price = num(spec.pack_price);
    const cpu = cov ? price / cov : 0;
    const unit = spec.pack_unit === "m" ? "m" : spec.pack_unit === "pz" ? t("packUnitPz") : "m²";
    return (
      <div className={`border border-border border-l-4 ${accent} bg-card`}>
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <span className="flex items-center gap-2 font-mono text-xs truncate">
            <span className={`h-2 w-2 shrink-0 ${side === "a" ? "bg-info" : "bg-primary"}`} />
            {product.brand} · {product.name}
          </span>
          {Object.keys(ov).length > 0 && (
            <button className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground" onClick={() => setOv({})}>
              <RotateCcw className="h-3 w-3" /> {t("reset")}
            </button>
          )}
        </div>
        <div className="space-y-3 p-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <EditInput testid={`edit-packprice-${side}`} label={t("packPrice")} suffix="CHF" value={spec.pack_price} onChange={(v) => set("pack_price", v)} />
            <ReadField testid={`read-coverage-${side}`} label={`${t("coveragePerPack")} (${unit})`} value={cov ? fmt(cov, 2) : "—"} suffix={unit} />
          </div>
          <ReadField testid={`read-costunit-${side}`} label={`${t("costPerUnit")} (${unit})`} value={cpu ? chf(cpu) : "—"} />
          <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <Lock className="h-3 w-3" /> {t("readonlyHint")}
          </p>
          {onGenerateQuote && (
            <Button
              data-testid={`generate-quote-${side}`}
              onClick={onGenerateQuote}
              className="w-full rounded-none bg-foreground font-semibold text-background hover:opacity-90"
            >
              <FileDown className="mr-2 h-4 w-4" /> {t("useForQuote")}
            </Button>
          )}
        </div>
      </div>
    );
  }

  const bagPrice = num(spec.price_per_kg) * num(spec.bag_weight_kg);
  const cons = effectiveConsumption(spec, cparams);
  const resa = cons ? num(spec.bag_weight_kg) / cons : 0;
  const resaVal = ov.resa_override !== undefined && ov.resa_override !== "" ? ov.resa_override : (resa ? Number(resa.toFixed(2)) : "");
  const isMembrane = spec.spessore_2mani_mm !== undefined && spec.spessore_2mani_mm !== null && spec.spessore_2mani_mm !== "";
  const spess = num(spec.spessore_2mani_mm);
  const consLabel = isMembrane
    ? `${t("consumption")} (${spess || 1}mm)`
    : cparams.mode === "thickness"
    ? `${t("consumption")} (${num(cparams.thickness) || 1}mm)`
    : cparams.mode === "grout"
      ? `${t("consumption")} (${t("groutJoint")})`
      : cparams.mode === "flat"
        ? t("consumption")
        : `${t("consumption")} (${num(cparams.notch) || 6}mm)`;
  return (
    <div className={`border border-border border-l-4 ${accent} bg-card`}>
      <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
        <span className="flex items-center gap-2 font-mono text-xs truncate">
          <span className={`h-2 w-2 shrink-0 ${side === "a" ? "bg-info" : "bg-primary"}`} />
          {product.brand} · {product.name}
        </span>
        {Object.keys(ov).length > 0 && (
          <button className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground" onClick={() => setOv({})}>
            <RotateCcw className="h-3 w-3" /> {t("reset")}
          </button>
        )}
      </div>
      {!ok && (
        <div className="flex items-start gap-2 border-b border-border bg-primary/10 px-4 py-2.5 text-xs">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <span>{t("incompleteBody")}</span>
        </div>
      )}
      <div className="space-y-3 p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <EditInput testid={`edit-price-${side}`} label={t("pricePerKg")} suffix="CHF/kg" value={spec.price_per_kg} onChange={(v) => set("price_per_kg", v)} />
          <ReadField label={t("priceBag")} value={bagPrice ? fmt(bagPrice, 2) : "—"} suffix="CHF" />
        </div>
        {isMembrane && (
          <div className="border border-primary/40 bg-primary/[0.06] p-2.5">
            <EditInput testid={`edit-spessore-${side}`} label={t("spessore2mani")} suffix="mm (spessore pieno · 2 mani)" value={spec.spessore_2mani_mm} onChange={(v) => set("spessore_2mani_mm", v)} />
          </div>
        )}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <ReadField testid={`read-consumption-${side}`} label={consLabel} value={cons ? fmt(cons, 2) : "—"} suffix="kg/m²" />
          <ReadField testid={`read-bagweight-${side}`} label={t("bagWeight")} value={spec.bag_weight_kg ?? "—"} suffix="kg" />
          <EditInput testid={`edit-resa-${side}`} label={t("resa")} value={resaVal} onChange={(v) => set("resa_override", v)} />
        </div>
        <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Lock className="h-3 w-3" /> {t("readonlyHint")}
        </p>
        {onGenerateQuote && (
          <Button
            data-testid={`generate-quote-${side}`}
            onClick={onGenerateQuote}
            className="w-full rounded-none bg-foreground font-semibold text-background hover:opacity-90"
          >
            <FileDown className="mr-2 h-4 w-4" /> {t("useForQuote")}
          </Button>
        )}
      </div>
    </div>
  );
}

function ReadField({ label, value, suffix, testid }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="flex items-center gap-1 label-caps text-muted-foreground truncate">
        <Lock className="h-2.5 w-2.5" /> {label}
      </span>
      <div className="flex items-center border border-dashed border-border bg-surface">
        <span data-testid={testid} className="w-full truncate px-2.5 py-2 font-mono text-sm text-muted-foreground">{value}</span>
        {suffix && <span className="px-1.5 font-mono text-[10px] text-muted-foreground/70">{suffix}</span>}
      </div>
    </div>
  );
}

function EditInput({ label, suffix, value, onChange, testid }) {
  return (
    <label className="group flex flex-col gap-1.5">
      <span className="flex items-center gap-1 label-caps text-primary">
        <Pencil className="h-2.5 w-2.5" /> {label}
      </span>
      <div className="flex items-center border border-primary/40 bg-primary/[0.06] transition-colors focus-within:border-primary focus-within:ring-1 focus-within:ring-primary group-hover:border-primary/70">
        <input
          data-testid={testid}
          type="number"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent px-2.5 py-2 font-mono text-sm font-semibold outline-none"
        />
        {suffix && <span className="px-2 font-mono text-[10px] text-muted-foreground">{suffix}</span>}
      </div>
    </label>
  );
}

function ParamCard({ icon: Icon, label, desc, unit, value, onChange, testid, action, highlight }) {
  return (
    <div className={`flex flex-col p-3 ${highlight ? "border-2 border-primary bg-primary/10" : "border border-primary/30 bg-primary/[0.04]"}`}>
      <div className="mb-1 flex items-center gap-2">
        <span className={`flex items-center justify-center bg-primary/15 text-primary ${highlight ? "h-7 w-7" : "h-6 w-6"}`}>
          <Icon className={highlight ? "h-4 w-4" : "h-3.5 w-3.5"} />
        </span>
        <span className={highlight ? "text-sm font-bold" : "text-xs font-semibold"}>{label}</span>
        {action && <span className="ml-auto">{action}</span>}
      </div>
      {desc && <p className="mb-2 text-[11px] leading-snug text-muted-foreground">{desc}</p>}
      <div className={`mt-auto flex items-center border bg-background transition-colors focus-within:border-primary focus-within:ring-1 focus-within:ring-primary ${highlight ? "border-primary" : "border-primary/40"}`}>
        <input
          data-testid={testid}
          type="number"
          min="0"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full bg-transparent px-2.5 font-mono outline-none ${highlight ? "py-2.5 text-lg font-bold" : "py-2 text-sm font-semibold"}`}
        />
      </div>
      {unit && <span className="mt-1 font-mono text-[10px] text-muted-foreground/70">{unit}</span>}
    </div>
  );
}

const NOTCH_OPTIONS = [3, 4, 6, 8, 10, 12, 15, 20];

function TrowelCard({ label, desc, unit, value, onChange }) {
  return (
    <div className="flex flex-col border border-primary/30 bg-primary/[0.04] p-3">
      <div className="mb-1 flex items-center gap-2">
        <span className="flex h-6 w-6 items-center justify-center bg-primary/15 text-primary">
          <Grip className="h-3.5 w-3.5" />
        </span>
        <span className="text-xs font-semibold">{label}</span>
      </div>
      {desc && <p className="mb-2 text-[11px] leading-snug text-muted-foreground">{desc}</p>}
      <div className="mt-auto flex flex-wrap gap-1">
        {NOTCH_OPTIONS.map((n) => (
          <button
            key={n}
            data-testid={`notch-${n}`}
            onClick={() => onChange(n)}
            className={`min-w-9 border px-2 py-1.5 font-mono text-xs font-semibold transition-colors ${
              num(value) === n
                ? "border-primary bg-primary text-primary-foreground"
                : "border-primary/40 bg-background text-muted-foreground hover:border-primary"
            }`}
          >
            {n}
          </button>
        ))}
      </div>
      {unit && <span className="mt-1 font-mono text-[10px] text-muted-foreground/70">{unit}</span>}
    </div>
  );
}

function CostComposition({ resA, resB, productA, productB, t }) {
  const items = [
    { key: "product", label: t("productCost"), icon: Package, color: COST_COLORS.product },
    { key: "transport", label: t("transportCost"), icon: Truck, color: COST_COLORS.transport },
    { key: "mixing", label: t("mixingCost"), icon: FlaskConical, color: COST_COLORS.mixing },
    { key: "pallet", label: t("palletCost"), icon: Warehouse, color: COST_COLORS.pallet },
  ];
  const seg = (r) => [r.productCost, r.transportCost, r.mixingCost, r.palletCost];
  return (
    <div className="border border-border bg-card" data-testid="cost-composition">
      <div className="flex items-center gap-2 border-b border-border bg-surface px-4 py-2.5">
        <PieChart className="h-4 w-4 text-primary" />
        <span className="label-caps text-foreground">{t("costComposition")}</span>
      </div>
      <div className="space-y-4 p-4">
        <CompBar name={`${productA.brand} ${productA.name}`} accent="text-info" total={resA.total} vals={seg(resA)} items={items} />
        <CompBar name={`${productB.brand} ${productB.name}`} accent="text-primary" total={resB.total} vals={seg(resB)} items={items} />
        <div className="flex flex-wrap gap-x-4 gap-y-2 border-t border-border pt-3">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <span key={it.key} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className="h-3 w-3 shrink-0" style={{ background: it.color }} />
                <Icon className="h-3.5 w-3.5" /> {it.label}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function CompBar({ name, accent, total, vals, items }) {
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-3">
        <span className={`truncate font-mono text-xs font-semibold ${accent}`}>{name}</span>
        <span className="shrink-0 font-mono text-xs font-bold">{chf(total)}</span>
      </div>
      <div className="flex h-7 w-full overflow-hidden border border-border">
        {vals.map((v, i) => {
          const pct = total > 0 ? (v / total) * 100 : 0;
          if (pct <= 0) return null;
          return (
            <div
              key={i}
              style={{ width: `${pct}%`, background: items[i].color }}
              title={`${items[i].label}: ${chf(v)} (${fmt(pct, 1)}%)`}
              className="h-full transition-all duration-500"
            />
          );
        })}
      </div>
    </div>
  );
}

function PackComparison({ specA, specB, productA, productB, t, onGenerateQuote }) {
  const [qty, setQty] = useState(10);
  const [priceA, setPriceA] = useState("");
  const [priceB, setPriceB] = useState("");
  useEffect(() => { setPriceA(specA.pack_price ?? ""); }, [productA?.id]);
  useEffect(() => { setPriceB(specB.pack_price ?? ""); }, [productB?.id]);

  const sA = { ...specA, pack_price: priceA === "" ? 0 : num(priceA) };
  const sB = { ...specB, pack_price: priceB === "" ? 0 : num(priceB) };
  const okA = isPackComputable(sA);
  const okB = isPackComputable(sB);
  const rA = computePack(sA, qty);
  const rB = computePack(sB, qty);
  const sameUnit = rA.unit === rB.unit;
  const both = okA && okB && num(qty) > 0 && sameUnit;
  const winner = both ? (rA.total <= rB.total ? "a" : "b") : null;
  const diff = Math.abs(rA.total - rB.total);
  const cheaper = winner === "a" ? productA : productB;
  const uLabel = (u) => (u === "m2" ? "m²" : u === "m" ? "m" : t("packUnitPz"));
  const unit = uLabel(rA.unit);

  const exportPackPDF = () => {
    const doc = new jsPDF();
    const W = doc.internal.pageSize.getWidth();
    doc.setFillColor(255, 192, 0); doc.rect(0, 0, W, 22, "F");
    doc.setTextColor(17, 17, 17); doc.setFontSize(18); doc.setFont(undefined, "bold");
    doc.text("METEROX", 14, 14);
    doc.setFontSize(10); doc.setFont(undefined, "normal");
    doc.text(t("packTitle"), W - 14, 14, { align: "right" });
    doc.setTextColor(60, 60, 60); doc.setFontSize(9);
    doc.text(new Date().toLocaleString(), 14, 30);
    const A = `${productA.brand} ${productA.name}`;
    const B = `${productB.brand} ${productB.name}`;
    let startY = 40;
    if (both) {
      doc.setTextColor(0, 150, 60); doc.setFontSize(12); doc.setFont(undefined, "bold");
      doc.text(`${t("winner")}: ${winner === "a" ? A : B}  ·  ${t("finalCost")} ${chf(winner === "a" ? rA.total : rB.total)}  ·  ${t("savings")} ${chf(diff)}`, 14, 39);
      startY = 46;
    } else if (!sameUnit && okA && okB) {
      doc.setTextColor(180, 100, 0); doc.setFontSize(9); doc.setFont(undefined, "normal");
      doc.text(doc.splitTextToSize(t("packDiffUnit"), W - 28), 14, 39);
      startY = 48;
    }
    autoTable(doc, {
      startY,
      head: [[`${t("packQty")}: ${fmt(qty, 0)} ${unit}`, A, B]],
      body: [
        [`${t("coveragePerPack")} (${unit})`, okA ? fmt(rA.coverage, 2) : "-", okB ? fmt(rB.coverage, 2) : "-"],
        [t("packsNeeded"), okA ? fmt(rA.packs, 1) : "-", okB ? fmt(rB.packs, 1) : "-"],
        [t("packPrice"), okA ? chf(rA.price) : "-", okB ? chf(rB.price) : "-"],
        [t("totalCost"), okA ? chf(rA.total) : "-", okB ? chf(rB.total) : "-"],
        [`${t("costPerUnit")} (${unit})`, okA ? chf(rA.costPerUnit) : "-", okB ? chf(rB.costPerUnit) : "-"],
      ],
      theme: "grid",
      headStyles: { fillColor: [17, 17, 17], textColor: 255, fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      columnStyles: { 1: { halign: "right" }, 2: { halign: "right" } },
    });
    const yn = (v) => (v === true || v === "true" ? t("yes") : t("no"));
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 6,
      head: [[t("techData"), A, B]],
      body: [
        [t("certificazioni"), specA.certificazioni || "-", specB.certificazioni || "-"],
        [t("interno"), yn(specA.interno), yn(specB.interno)],
        [t("esterno"), yn(specA.esterno), yn(specB.esterno)],
        [t("allungamento"), specA.allungamento_pct ?? "-", specB.allungamento_pct ?? "-"],
      ],
      theme: "grid",
      headStyles: { fillColor: [17, 17, 17], textColor: 255, fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      columnStyles: { 1: { halign: "right" }, 2: { halign: "right" } },
    });
    doc.save(`METEROX_${productA.name}_vs_${productB.name}.pdf`.replace(/\s+/g, "_"));
  };

  return (
    <div className="space-y-4" data-testid="pack-comparison">
      <div className="flex justify-end">
        <Button
          data-testid="export-pack-pdf-btn"
          onClick={exportPackPDF}
          variant="outline"
          className="rounded-none border-border font-semibold"
        >
          <FileDown className="mr-2 h-4 w-4" /> {t("exportPdf")}
        </Button>
      </div>
      {both && (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border border-success bg-success/10 p-4" data-testid="pack-verdict">
          <Trophy className="h-6 w-6 shrink-0 text-success" />
          <div>
            <div className="label-caps text-success">{t("winner")}</div>
            <div className="font-display text-2xl font-black text-foreground">{cheaper.brand} {cheaper.name}</div>
          </div>
          <div className="ml-auto text-right">
            <div className="label-caps text-muted-foreground">{t("finalCost")}</div>
            <div className="font-mono text-xl font-bold text-foreground">{chf(winner === "a" ? rA.total : rB.total)}</div>
          </div>
          <div className="text-right">
            <div className="label-caps text-muted-foreground">{t("savings")}</div>
            <div className="font-mono text-xl font-bold text-success">−{chf(diff)}</div>
          </div>
        </div>
      )}

      {!sameUnit && okA && okB && (
        <div className="flex items-start gap-2 border border-primary bg-primary/10 px-4 py-3 text-sm" data-testid="pack-diff-unit">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <span>{t("packDiffUnit")}</span>
        </div>
      )}

      <div className="border border-border bg-card">
        <div className="flex items-center gap-2 border-b border-border bg-surface px-4 py-2.5">
          <Package className="h-4 w-4 text-primary" />
          <span className="label-caps text-foreground">{t("packTitle")}</span>
        </div>
        <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-3">
          <ParamCard testid="input-pack-qty" icon={Ruler} label={`${t("packQty")} (${unit})`} desc={t("packQtyDesc")} unit={unit} value={qty} onChange={setQty} />
          <PackPriceCard testid="input-pack-price-a" label={`${t("packPrice")} · A`} value={priceA} onChange={setPriceA} />
          <PackPriceCard testid="input-pack-price-b" label={`${t("packPrice")} · B`} value={priceB} onChange={setPriceB} />
        </div>
        <div className="grid grid-cols-1 gap-3 border-t border-border p-4 sm:grid-cols-2">
          <Button data-testid="generate-quote-a" onClick={() => onGenerateQuote(productA, sA, { area: num(qty) })} className="w-full rounded-none bg-foreground font-semibold text-background hover:opacity-90">
            <FileDown className="mr-2 h-4 w-4" /> {t("useForQuote")} · A
          </Button>
          <Button data-testid="generate-quote-b" onClick={() => onGenerateQuote(productB, sB, { area: num(qty) })} className="w-full rounded-none bg-foreground font-semibold text-background hover:opacity-90">
            <FileDown className="mr-2 h-4 w-4" /> {t("useForQuote")} · B
          </Button>
        </div>
      </div>

      <div className="border border-border bg-card overflow-x-auto">
        <div className="min-w-[480px]">
          <div className="grid grid-cols-[1.4fr,1fr,1fr] border-b border-border bg-surface">
            <div className="flex items-center gap-2 px-4 py-2.5 label-caps text-muted-foreground"><Calculator className="h-4 w-4 text-primary" />{t("comparison")}</div>
            <HeaderCol name={productA?.name} accent="text-info" isWinner={winner === "a"} />
            <HeaderCol name={productB?.name} accent="text-primary" isWinner={winner === "b"} />
          </div>
          <CostRow testid="row-coverage" label={`${t("coveragePerPack")} (${unit})`} a={fmt(rA.coverage, 2)} b={fmt(rB.coverage, 2)} okA={okA} okB={okB} />
          <CostRow testid="row-packs" label={t("packsNeeded")} a={fmt(rA.packs, 1)} b={fmt(rB.packs, 1)} okA={okA} okB={okB} />
          <CostRow testid="row-pack-price" icon={Package} color={COST_COLORS.product} label={t("packPrice")} a={chf(rA.price)} b={chf(rB.price)} okA={okA} okB={okB} lowA={rA.price} lowB={rB.price} compare />
          <Divider />
          <TotalRow label={t("totalCost")} a={rA.total} b={rB.total} okA={okA} okB={okB} winner={winner} />
          <TotalRow label={`${t("costPerUnit")} (${unit})`} a={rA.costPerUnit} b={rB.costPerUnit} okA={okA} okB={okB} winner={winner} perM2 />
        </div>
      </div>
    </div>
  );
}

function PackPriceCard({ testid, label, value, onChange }) {
  return (
    <div className="border border-border bg-surface p-3">
      <div className="mb-1.5 flex items-center gap-2">
        <Coins className="h-4 w-4 text-primary" />
        <span className="label-caps text-muted-foreground">{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="font-mono text-xs text-muted-foreground">CHF</span>
        <Input
          data-testid={testid}
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="rounded-none border-border bg-card font-mono"
        />
      </div>
    </div>
  );
}

function EquivalenceBanner({ info, t, onPickB }) {
  const equivalent = info.equivalent;
  const reasons = equivReasonText(info.reasons, t);
  const similar = info.match;

  return (
    <div className="border border-border bg-card">
      <div
        data-testid="equivalence-banner"
        className={`flex items-start gap-2 px-4 py-3 text-sm ${equivalent ? "bg-success/10" : "bg-primary/10"}`}
      >
        {equivalent
          ? <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
          : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-primary" />}
        <span>{equivalent ? t("equivSame") : `${t("equivDiff")} (${reasons.join(", ")}).`}</span>
      </div>
      {!equivalent && similar && (
        <div className="flex flex-wrap items-center gap-2 border-t border-border bg-info/10 px-4 py-3 text-sm" data-testid="similar-suggestion">
          <Sparkles className="h-4 w-4 shrink-0 text-info" />
          <span>{t("suggestSimilar")} <b>{similar.brand}</b>: <b>{similar.name}</b></span>
          <button
            data-testid="use-similar-btn"
            onClick={() => onPickB && onPickB(similar)}
            className="ml-auto border border-info bg-info px-2.5 py-1.5 text-xs font-semibold text-white hover:opacity-90"
          >
            {t("useThis")}
          </button>
        </div>
      )}
    </div>
  );
}

function VersusCharts({ resA, resB, specA, specB, productA, productB, t }) {
  const anyPack = resA.isPack || resB.isPack;

  // core (cost/yield/time) metrics
  const core = [
    { key: "priceKg", label: t("pricePerKg"), abbr: "CHF/kg", a: num(specA.price_per_kg), b: num(specB.price_per_kg), better: "low", fmt: (v) => chf(v) },
    { key: "costM2", label: t("costPerM2"), abbr: "CHF/m²", a: resA.costPerM2, b: resB.costPerM2, better: "low", fmt: (v) => chf(v) },
    { key: "resa", label: t("resa"), abbr: t("rAxYield"), a: resA.resa, b: resB.resa, better: "high", fmt: (v) => fmt(v, 2) + " m²" },
    { key: "consumo", label: t("consumption"), abbr: t("rAxConsumo"), a: resA.consumption, b: resB.consumption, better: "low", fmt: (v) => fmt(v, 2) + " kg/m²" },
    { key: "lav", label: t("lavorabilita"), abbr: t("rAxWork"), a: num(specA.lavorabilita_min), b: num(specB.lavorabilita_min), better: "high", fmt: (v) => fmt(v, 0) + " min" },
    { key: "ped", label: t("pedonabile"), abbr: t("rAxWalk"), a: num(specA.pedonabile_ore), b: num(specB.pedonabile_ore), better: "low", fmt: (v) => fmt(v, 0) + " h" },
  ]
    .filter((m) => !(anyPack && (m.key === "priceKg" || m.key === "resa" || m.key === "consumo")))
    .filter((m) => m.a > 0 || m.b > 0);

  // Dynamic final technical block: [Spessore max, <category-specific>] shown as a
  // pair to keep a clean 4x2 grid, or hidden entirely when no meaningful data.
  const dyn = buildDynamicMetric(specA, specB, productA, productB, t);
  let finalBlock = [];
  if (dyn) {
    const spDeclA = num(specA.spessore_max_mm) > 0, spDeclB = num(specB.spessore_max_mm) > 0;
    const spess = { key: "spess", label: t("axThickMm"), abbr: t("rAxThick"), a: num(specA.spessore_max_mm), b: num(specB.spessore_max_mm),
      better: "high", fmt: (v) => fmt(v, 0) + " mm", declA: spDeclA, declB: spDeclB };
    finalBlock = [spess, dyn];
  }
  const metrics = [...core, ...finalBlock];

  let winsA = 0, winsB = 0;
  metrics.forEach((m) => {
    if (m.neutral || (m.declA === false) || (m.declB === false)) return;
    if (!(m.a > 0 && m.b > 0)) return;
    const aWin = m.better === "low" ? m.a < m.b : m.a > m.b;
    const bWin = m.better === "low" ? m.b < m.a : m.b > m.a;
    if (aWin) winsA += 1; else if (bWin) winsB += 1;
  });

  return (
    <div className="border border-border bg-card" data-testid="versus-charts">
      <div className="flex flex-wrap items-center gap-2 border-b border-border bg-surface px-4 py-2.5">
        <BarChart3 className="h-4 w-4 text-primary" />
        <span className="label-caps text-foreground">{t("versusTitle")}</span>
      </div>

      {/* VS win tally */}
      <div className="grid grid-cols-[1fr,auto,1fr] items-center gap-3 border-b border-border p-4">
        <div className="min-w-0 text-right">
          <div className="truncate font-mono text-xs" style={{ color: "hsl(var(--info))" }}>{productA.brand} {productA.name}</div>
          <div className="font-display text-3xl font-black" style={{ color: "hsl(var(--info))" }} data-testid="wins-a">{winsA}</div>
          <div className="label-caps text-muted-foreground">{t("versusWins")} {winsA} {t("winsUnit")}</div>
        </div>
        <div className="flex h-10 w-10 items-center justify-center bg-foreground font-display text-sm font-black text-background">VS</div>
        <div className="min-w-0 text-left">
          <div className="truncate font-mono text-xs text-primary">{productB.brand} {productB.name}</div>
          <div className="font-display text-3xl font-black text-primary" data-testid="wins-b">{winsB}</div>
          <div className="label-caps text-muted-foreground">{t("versusWins")} {winsB} {t("winsUnit")}</div>
        </div>
      </div>

      <RadarChart metrics={metrics} productA={productA} productB={productB} t={t} />

      <div className="grid gap-x-8 gap-y-5 p-4 sm:grid-cols-2">
        {metrics.map((m) => <MetricRow key={m.key} m={m} t={t} />)}
      </div>
    </div>
  );
}

function RadarChart({ metrics, productA, productB, t }) {
  if (metrics.length < 3) return null;
  const size = 560, cx = size / 2, cy = size / 2, R = 188;
  const n = metrics.length;
  const norm = (m, side) => {
    const max = Math.max(m.a, m.b) || 1, min = Math.min(m.a, m.b);
    const v = side === "a" ? m.a : m.b;
    if (!v) return 0.08;
    const x = m.better === "high" ? v / max : (min || v) / v;
    return Math.max(0.08, Math.min(1, x));
  };
  const pt = (i, r) => {
    const ang = -Math.PI / 2 + (i * 2 * Math.PI) / n;
    return [cx + R * r * Math.cos(ang), cy + R * r * Math.sin(ang)];
  };
  const poly = (side) => metrics.map((m, i) => pt(i, norm(m, side)).join(",")).join(" ");
  const COL_A = "#2f9bff", COL_B = "#ffc400";

  return (
    <div className="flex flex-col items-center border-b border-border px-2 py-4" data-testid="radar-chart">
      <span className="mb-1 self-start label-caps text-muted-foreground">{t("radarTitle")}</span>
      <svg viewBox={`-92 -30 ${size + 184} ${size + 70}`} className="w-full max-w-2xl">
        {[0.25, 0.5, 0.75, 1].map((g, gi) => (
          <polygon key={gi} points={metrics.map((_, i) => pt(i, g).join(",")).join(" ")}
            fill="none" stroke="hsl(var(--border))" strokeWidth="1" />
        ))}
        {metrics.map((_, i) => {
          const [x, y] = pt(i, 1);
          return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="hsl(var(--border))" strokeWidth="1" />;
        })}
        <polygon points={poly("a")} fill={COL_A} fillOpacity="0.32" stroke={COL_A} strokeWidth="3" strokeLinejoin="round" />
        <polygon points={poly("b")} fill={COL_B} fillOpacity="0.32" stroke={COL_B} strokeWidth="3" strokeLinejoin="round" />
        {/* vertex dots */}
        {metrics.map((m, i) => {
          const [ax, ay] = pt(i, norm(m, "a"));
          const [bx, by] = pt(i, norm(m, "b"));
          return (
            <g key={`d${i}`}>
              <circle cx={ax} cy={ay} r="4" fill={COL_A} stroke="hsl(var(--card))" strokeWidth="1.5" />
              <circle cx={bx} cy={by} r="4" fill={COL_B} stroke="hsl(var(--card))" strokeWidth="1.5" />
            </g>
          );
        })}
        {/* axis label + values */}
        {metrics.map((m, i) => {
          const ang = -Math.PI / 2 + (i * 2 * Math.PI) / n;
          const cos = Math.cos(ang), sin = Math.sin(ang);
          const lx = cx + R * 1.16 * cos;
          const ly = cy + R * 1.16 * sin - (sin < -0.3 ? 20 : 0);
          const anchor = cos > 0.25 ? "start" : cos < -0.25 ? "end" : "middle";
          return (
            <text key={i} x={lx} y={ly} textAnchor={anchor} fontFamily="IBM Plex Mono">
              <tspan x={lx} dy="0" fontSize="13" fontWeight="700" fill="hsl(var(--muted-foreground))">{m.abbr}</tspan>
              <tspan x={lx} dy="17" fontSize="14" fontWeight="700" fill={COL_A}>{m.a > 0 ? m.fmt(m.a) : "—"}</tspan>
              <tspan x={lx} dy="17" fontSize="14" fontWeight="700" fill={COL_B}>{m.b > 0 ? m.fmt(m.b) : "—"}</tspan>
            </text>
          );
        })}
      </svg>
      <div className="mt-2 flex flex-wrap justify-center gap-5 text-sm">
        <span className="flex items-center gap-1.5"><span className="h-3 w-3" style={{ background: COL_A }} /> {productA.name}</span>
        <span className="flex items-center gap-1.5"><span className="h-3 w-3" style={{ background: COL_B }} /> {productB.name}</span>
      </div>
    </div>
  );
}

function MetricRow({ m, t }) {
  const declA = m.declA === undefined ? true : m.declA;
  const declB = m.declB === undefined ? true : m.declB;
  const neutral = !!m.neutral;
  const max = Math.max(declA ? m.a : 0, declB ? m.b : 0) || 1;
  const winA = !neutral && declA && declB && m.a > 0 && m.b > 0 && (m.better === "low" ? m.a < m.b : m.a > m.b);
  const winB = !neutral && declA && declB && m.a > 0 && m.b > 0 && (m.better === "low" ? m.b < m.a : m.b > m.a);
  const bar = (val, decl, win, color) => (
    <div className="flex items-center gap-2">
      {decl ? (
        <>
          <div className="relative h-5 flex-1 overflow-hidden bg-surface">
            <div className="h-full transition-all duration-500" style={{ width: `${max ? (val / max) * 100 : 0}%`, background: color, opacity: win ? 1 : 0.55 }} />
          </div>
          <span className={`w-24 shrink-0 text-right font-mono text-xs ${win ? "font-bold" : "text-muted-foreground"}`}>
            {win && <Trophy className="mr-1 inline h-3 w-3 text-success" />}{m.fmt(val)}
          </span>
        </>
      ) : (
        <div className="flex h-5 flex-1 items-center bg-surface/50 px-2" style={{ borderLeft: `3px solid ${color}` }}>
          <span className="font-mono text-[10px] italic text-muted-foreground">{t("noData")}</span>
        </div>
      )}
    </div>
  );
  return (
    <div data-testid={`metric-${m.key}`}>
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-xs font-semibold">{m.label}</span>
        <span className="label-caps text-muted-foreground">{neutral ? "" : (m.better === "low" ? t("lowerBetter") : t("higherBetter"))}</span>
      </div>
      <div className="space-y-1">
        {bar(m.a, declA, winA, "hsl(var(--info))")}
        {bar(m.b, declB, winB, "hsl(var(--primary))")}
      </div>
    </div>
  );
}

function CostRow({ label, a, b, okA, okB, lowA, lowB, compare, icon: Icon, color, testid }) {
  const winA = compare && okA && okB && lowA < lowB;
  const winB = compare && okA && okB && lowB < lowA;
  return (
    <div className="grid grid-cols-[1.4fr,1fr,1fr] border-b border-border/60 text-sm">
      <div className="flex items-center gap-2 px-4 py-2 text-muted-foreground">
        {color && <span className="h-2.5 w-2.5 shrink-0" style={{ background: color }} />}
        {Icon && <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
        <span>{label}</span>
      </div>
      <Cell val={okA ? a : "—"} win={winA} testid={testid ? `${testid}-a` : undefined} />
      <Cell val={okB ? b : "—"} win={winB} testid={testid ? `${testid}-b` : undefined} />
    </div>
  );
}

function TotalRow({ label, a, b, okA, okB, winner, perM2 }) {
  const flashA = useFlash(Math.round(a * 100));
  const flashB = useFlash(Math.round(b * 100));
  return (
    <div className="grid grid-cols-[1.4fr,1fr,1fr] border-b border-border bg-surface text-sm">
      <div className="px-4 py-3 font-semibold">{label}</div>
      <div className={`px-4 py-3 text-right font-mono font-bold ${winner === "a" ? "text-success" : ""} ${flashA ? "flash-cell" : ""}`} data-testid={`total-a${perM2 ? "-m2" : ""}`}>
        {okA ? (perM2 ? chf(a) : chf(a)) : "—"}
      </div>
      <div className={`px-4 py-3 text-right font-mono font-bold ${winner === "b" ? "text-success" : ""} ${flashB ? "flash-cell" : ""}`} data-testid={`total-b${perM2 ? "-m2" : ""}`}>
        {okB ? chf(b) : "—"}
      </div>
    </div>
  );
}

function Cell({ val, win, testid }) {
  return (
    <div data-testid={testid} className={`px-4 py-2 text-right font-mono ${win ? "bg-success/10 font-semibold text-success" : ""}`}>
      {val}
    </div>
  );
}

function TechRow({ label, a, b, bool }) {
  const render = (v) => {
    if (v === undefined || v === null || v === "") return <span className="text-muted-foreground/40">—</span>;
    if (bool && React.isValidElement(v)) return v;
    return <span>{v}</span>;
  };
  return (
    <div className="grid grid-cols-[1.4fr,1fr,1fr] border-b border-border/40 text-sm last:border-b-0">
      <div className="px-4 py-2 text-muted-foreground">{label}</div>
      <div className="px-4 py-2 text-right font-mono">{render(a)}</div>
      <div className="px-4 py-2 text-right font-mono">{render(b)}</div>
    </div>
  );
}

function Divider() {
  return <div className="h-px bg-border" />;
}

function rangeVal(a, b) {
  const x = num(a), y = num(b);
  if (!x && !y) return "";
  if (x && y) return `${fmt(x, 1)} – ${fmt(y, 1)}`;
  return fmt(x || y, 1);
}

function boolVal(v, t) {
  if (v === undefined || v === null || v === "") return null;
  const yes = v === true || v === "true" || v === 1;
  return yes ? (
    <span className="inline-flex items-center gap-1 text-success"><Check className="h-3.5 w-3.5" /> {t("yes")}</span>
  ) : (
    <span className="inline-flex items-center gap-1 text-muted-foreground/60"><X className="h-3.5 w-3.5" /> {t("no")}</span>
  );
}

const _truthy = (v) => v === true || v === "true" || v === 1;

// Application field: outdoor products are usable indoors too
function ambitoText(s, t) {
  if (_truthy(s.esterno)) return t("ambitoIntExt");
  if (_truthy(s.interno)) return t("ambitoInt");
  return "";
}

// Where a repair mortar can be applied (floor is always possible)
function applicazioneText(s, t) {
  const parts = [t("posPavimento")];
  if (_truthy(s.pareti)) parts.push(t("posParete"));
  if (_truthy(s.soffitto)) parts.push(t("posSoffitto"));
  return parts.join(" · ");
}


/* ---------- single-product mode ---------- */

function SingleRow({ label, value, color, testid }) {
  return (
    <div className="grid grid-cols-[1.4fr,1fr] border-b border-border/60 text-sm">
      <div className="flex items-center gap-2 px-4 py-2 text-muted-foreground">
        {color && <span className="h-2.5 w-2.5 shrink-0" style={{ background: color }} />}
        <span>{label}</span>
      </div>
      <div data-testid={testid} className="px-4 py-2 text-right font-mono">{value}</div>
    </div>
  );
}

function SingleTechRow({ label, value }) {
  const render = (v) => {
    if (v === undefined || v === null || v === "") return <span className="text-muted-foreground/40">—</span>;
    return <span>{v}</span>;
  };
  return (
    <div className="grid grid-cols-[1.4fr,1fr] border-b border-border/40 text-sm last:border-b-0">
      <div className="px-4 py-2 text-muted-foreground">{label}</div>
      <div className="px-4 py-2 text-right font-mono">{render(value)}</div>
    </div>
  );
}

function TechTableSingle({ product, spec, t }) {
  const techIds = TECH_BY_SUB[product.subcategory] || TECH_LEGACY;
  return (
    <div className="border border-border bg-card overflow-x-auto">
      <div className="min-w-[320px]">
        <div className="grid grid-cols-[1.4fr,1fr] border-b border-border bg-surface">
          <div className="flex items-center gap-2 px-4 py-2.5 label-caps text-muted-foreground"><ClipboardList className="h-4 w-4 text-primary" />{t("techData")}</div>
          <div className="px-4 py-2.5 text-right font-mono text-xs truncate">{product.name}</div>
        </div>
        {techIds.map((id) => {
          const def = ROW_DEFS[id];
          if (!def) return null;
          const a = def.val(spec, t);
          if (a === undefined || a === null || a === "") return null;
          return <SingleTechRow key={id} label={t(id)} value={def.bool ? boolVal(a, t) : a} />;
        })}
      </div>
    </div>
  );
}

export function SingleView({ product, spec, ov, setOv, params, setParam, t, onGenerateQuote, onResult, packInit, simple }) {
  const mode = modeForProduct(spec, product.subcategory);
  const isPack = mode === "pack";
  const p = { ...params, mode };
  const ok = !isPack && isComputable(spec, mode);
  const res = isPack ? null : computeProduct(spec, p);
  const material = ok ? res.productCost : null;
  const areaVal = num(params.area_m2), priceVal = num(spec.price_per_kg);
  useEffect(() => {
    if (isPack || !onResult) return; // pack handled by SinglePackView's own effect
    onResult(ok ? { material, area: areaVal, price: priceVal, pack: false, mode, bags: res.bags, totalKg: res.totalKg } : null);
  }, [isPack, ok, material, areaVal, priceVal, mode, onResult]);

  if (isPack) {
    return <SinglePackView product={product} spec={spec} t={t} onGenerateQuote={onGenerateQuote} onResult={onResult} initialQty={packInit?.qty} initialPrice={packInit?.price} />;
  }

  const productPerM2 = ok && areaVal > 0 ? res.productCost / areaVal : 0;

  return (
    <div className="space-y-4" data-testid="single-view">
      <div className="border border-border bg-card" data-testid="site-params">
        <div className="flex flex-wrap items-center gap-2 border-b border-border bg-surface px-4 py-2.5">
          <SlidersHorizontal className="h-4 w-4 text-primary" />
          <span className="label-caps text-foreground">{t("advanced")}</span>
          <span className="ml-auto flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-1 text-[11px] font-semibold text-primary">
            <Pencil className="h-3 w-3" /> {t("editHint")}
          </span>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {mode === "notch" && (
              <TrowelCard label={t("trowel")} desc={t("trowelDesc")} unit={t("unitMm")} value={params.notch} onChange={(v) => setParam("notch", v)} />
            )}
            {mode === "thickness" && (
              <ParamCard testid="input-thickness" icon={Ruler} label={t("spessore")} unit={t("unitMmFull")} value={params.thickness} onChange={(v) => setParam("thickness", v)} />
            )}
            {mode === "grout" && (
              <>
                <ParamCard testid="input-tilea" icon={Grip} label={t("tileA")} desc={t("groutDesc")} unit={t("unitCm")} value={params.tileA} onChange={(v) => setParam("tileA", v)} />
                <ParamCard testid="input-tileb" icon={Grip} label={t("tileB")} unit={t("unitCm")} value={params.tileB} onChange={(v) => setParam("tileB", v)} />
                <ParamCard testid="input-tilethickness" icon={Ruler} label={t("tileThickness")} unit={t("unitMmFull")} value={params.tileThickness} onChange={(v) => setParam("tileThickness", v)} />
                <ParamCard testid="input-jointwidth" icon={Ruler} label={t("jointWidth")} unit={t("unitMmFull")} value={params.jointWidth} onChange={(v) => setParam("jointWidth", v)} />
              </>
            )}
            <ParamCard testid="input-metratura" icon={Ruler} label={t("metratura")} unit={t("unitM2")} value={params.area_m2} onChange={(v) => setParam("area_m2", v)} action={<SmartAreaButton onResult={(v) => setParam("area_m2", v)} />} highlight />
          </div>
          {!simple && (
            <div className="border-t border-dashed border-border pt-3">
              <span className="label-caps text-muted-foreground">{t("laborSection")}</span>
              <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <ParamCard testid="input-salary" icon={Coins} label={t("salary")} desc={t("descSalary")} unit={t("unitChfHour")} value={params.salary} onChange={(v) => setParam("salary", v)} />
                <ParamCard testid="input-transport" icon={Truck} label={t("transportMin")} desc={t("descTransport")} unit={t("unitMinBag")} value={params.transportMin} onChange={(v) => setParam("transportMin", v)} />
                <ParamCard testid="input-mixing" icon={FlaskConical} label={t("mixingMin")} desc={t("descMixing")} unit={t("unitMinBag")} value={params.mixingMin} onChange={(v) => setParam("mixingMin", v)} />
                <ParamCard testid="input-pallet" icon={Warehouse} label={t("palletMin")} desc={t("descPallet")} unit={t("unitMinPallet")} value={params.palletMin} onChange={(v) => setParam("palletMin", v)} />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <EditPanel side="a" product={product} spec={spec} ov={ov} setOv={setOv} ok={ok} t={t} cparams={p} onGenerateQuote={onGenerateQuote ? () => onGenerateQuote(product, spec) : undefined} />

        <div className="border border-border bg-card">
          <div className="flex items-center gap-2 border-b border-border bg-surface px-4 py-2.5">
            <Calculator className="h-4 w-4 text-primary" />
            <span className="label-caps text-foreground">{t("costBreakdown")}</span>
          </div>
          <SingleRow label={t("totalKg")} value={ok ? fmt(res.totalKg, 0) + " kg" : "—"} testid="single-totalkg" />
          <SingleRow label={t("bagsNeeded")} value={ok ? fmt(res.bags, 0) : "—"} testid="single-bags" />
          <SingleRow label={t("resa")} value={ok ? fmt(res.resa, 2) : "—"} testid="single-resa" />
          <SingleRow label={t("productCost")} value={ok ? chf(res.productCost) : "—"} color={COST_COLORS.product} />
          {!simple && <SingleRow label={t("transportCost")} value={ok ? chf(res.transportCost) : "—"} color={COST_COLORS.transport} />}
          {!simple && <SingleRow label={t("mixingCost")} value={ok ? chf(res.mixingCost) : "—"} color={COST_COLORS.mixing} />}
          {!simple && <SingleRow label={t("palletCost")} value={ok ? chf(res.palletCost) : "—"} color={COST_COLORS.pallet} />}
          {!simple && (
            <div className="grid grid-cols-[1.4fr,1fr] border-b border-border bg-surface text-sm">
              <div className="px-4 py-3 font-semibold">{t("totalCost")}</div>
              <div className="px-4 py-3 text-right font-mono font-bold" data-testid="single-total">{ok ? chf(res.total) : "—"}</div>
            </div>
          )}
          <div className="grid grid-cols-[1.4fr,1fr] text-sm">
            <div className="px-4 py-3 font-semibold">{t("costPerM2")}</div>
            <div className="px-4 py-3 text-right font-mono font-bold" data-testid="single-perm2">{ok ? chf(simple ? productPerM2 : res.costPerM2) : "—"}</div>
          </div>
        </div>
      </div>

      <TechTableSingle product={product} spec={spec} t={t} />
    </div>
  );
}

function SinglePackView({ product, spec, t, onGenerateQuote, onResult, initialQty, initialPrice }) {
  const [qty, setQty] = useState(initialQty ?? 10);
  const [price, setPrice] = useState(initialPrice != null ? String(initialPrice) : "");
  useEffect(() => { setPrice(initialPrice != null ? String(initialPrice) : (spec.pack_price ?? "")); }, [product?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const s = { ...spec, pack_price: price === "" ? 0 : num(price) };
  const ok = isPackComputable(s);
  const r = computePack(s, qty);
  const uLabel = (u) => (u === "m2" ? "m²" : u === "m" ? "m" : t("packUnitPz"));
  const unit = uLabel(r.unit);
  const total = ok ? r.total : null;
  useEffect(() => {
    if (onResult) onResult(ok ? { material: total, area: num(qty), price: num(price === "" ? 0 : price), pack: true, unit, packs: r.packs } : null);
  }, [ok, total, qty, price, unit, onResult]);
  return (
    <div className="space-y-4" data-testid="single-view">
      <div className="border border-border bg-card">
        <div className="flex items-center gap-2 border-b border-border bg-surface px-4 py-2.5">
          <Package className="h-4 w-4 text-primary" /><span className="label-caps text-foreground">{t("packTitle")}</span>
        </div>
        <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2">
          <ParamCard testid="input-pack-qty" icon={Ruler} label={`${t("packQty")} (${unit})`} desc={t("packQtyDesc")} unit={unit} value={qty} onChange={setQty} />
          <PackPriceCard testid="input-pack-price-a" label={t("packPrice")} value={price} onChange={setPrice} />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="border border-border bg-card">
          <div className="flex items-center gap-2 border-b border-border bg-surface px-4 py-2.5">
            <Calculator className="h-4 w-4 text-primary" /><span className="label-caps text-foreground">{t("costBreakdown")}</span>
          </div>
          <SingleRow label={`${t("coveragePerPack")} (${unit})`} value={ok ? fmt(r.coverage, 2) : "—"} />
          <SingleRow label={t("packsNeeded")} value={ok ? fmt(r.packs, 1) : "—"} testid="single-packs" />
          <SingleRow label={t("packPrice")} value={ok ? chf(r.price) : "—"} color={COST_COLORS.product} />
          <div className="grid grid-cols-[1.4fr,1fr] border-b border-border bg-surface text-sm">
            <div className="px-4 py-3 font-semibold">{t("totalCost")}</div>
            <div className="px-4 py-3 text-right font-mono font-bold" data-testid="single-total">{ok ? chf(r.total) : "—"}</div>
          </div>
          <div className="grid grid-cols-[1.4fr,1fr] text-sm">
            <div className="px-4 py-3 font-semibold">{`${t("costPerUnit")} (${unit})`}</div>
            <div className="px-4 py-3 text-right font-mono font-bold">{ok ? chf(r.costPerUnit) : "—"}</div>
          </div>
        </div>
        <div className="flex flex-col justify-end border border-border bg-card p-4">
          <p className="mb-3 flex items-center gap-1.5 text-[11px] text-muted-foreground"><Lock className="h-3 w-3" /> {t("readonlyHint")}</p>
          {onGenerateQuote && (
            <Button data-testid="generate-quote-a" onClick={() => onGenerateQuote(product, s, { area: num(qty) })} className="w-full rounded-none bg-foreground font-semibold text-background hover:opacity-90">
              <FileDown className="mr-2 h-4 w-4" /> {t("useForQuote")}
            </Button>
          )}
        </div>
      </div>
      <TechTableSingle product={product} spec={spec} t={t} />
    </div>
  );
}
